import axios from "axios";
import config from "services/config";

const AUTH_BASE = `${config.url}/portal/auth`;
const API_BASE = `${config.url}/portal`;

function getHeaders() {
  const portalData = JSON.parse(localStorage.getItem("userdata:portal"));
  return portalData ? { Authorization: `Bearer ${portalData.token}` } : {};
}

export class PortalService {
  // Auth (public)
  static async register({ id_number, email, password }) {
    const { data } = await axios.post(`${AUTH_BASE}/register`, { id_number, email, password });
    return data;
  }

  static async verifyEmail({ token }) {
    const { data } = await axios.post(`${AUTH_BASE}/verify-email`, { token });
    return data;
  }

  static async login({ email, password }) {
    const { data } = await axios.post(`${AUTH_BASE}/login`, { email, password });
    return data;
  }

  static async forgotPassword({ email }) {
    const { data } = await axios.post(`${AUTH_BASE}/forgot-password`, { email });
    return data;
  }

  static async resetPassword({ token, password }) {
    const { data } = await axios.post(`${AUTH_BASE}/reset-password`, { token, password });
    return data;
  }

  static async resendVerification({ email }) {
    const { data } = await axios.post(`${AUTH_BASE}/resend-verification`, { email });
    return data;
  }

  // Dashboard
  static async getDashboard() {
    const { data } = await axios.get(`${API_BASE}/dashboard`, { headers: getHeaders() });
    return data;
  }

  // Sprint 2 — KPIs en vivo (resultado neto, obligaciones tributarias, facturas pendientes, flujo de caja)
  static async getKpis({ year, month } = {}) {
    const params = new URLSearchParams();
    if (year) params.set("year", year);
    if (month) params.set("month", month);
    const qs = params.toString() ? `?${params}` : "";
    const { data } = await axios.get(`${API_BASE}/dashboard/kpis${qs}`, { headers: getHeaders() });
    return data;
  }

  // Sprint 2 — Alertas tipadas
  static async listAlerts({ status = "active" } = {}) {
    const { data } = await axios.get(`${API_BASE}/alerts?status=${status}`, { headers: getHeaders() });
    return data;
  }

  static async markAlertRead(id) {
    const { data } = await axios.put(`${API_BASE}/alerts/${id}/read`, {}, { headers: getHeaders() });
    return data;
  }

  static async dismissAlert(id) {
    const { data } = await axios.put(`${API_BASE}/alerts/${id}/dismiss`, {}, { headers: getHeaders() });
    return data;
  }

  static async recalculateAlerts() {
    const { data } = await axios.post(`${API_BASE}/alerts/recalculate`, {}, { headers: getHeaders() });
    return data;
  }

  // Sprint 3 — Recomendaciones del contador (vista cliente)
  static async listRecommendations({ unread, limit = 20 } = {}) {
    const params = new URLSearchParams();
    if (unread) params.set("unread", "true");
    params.set("limit", limit);
    const { data } = await axios.get(`${API_BASE}/recommendations?${params}`, { headers: getHeaders() });
    return data;
  }

  static async markRecommendationRead(id) {
    const { data } = await axios.put(`${API_BASE}/recommendations/${id}/read`, {}, { headers: getHeaders() });
    return data;
  }

  // Profile
  static async getProfile() {
    const { data } = await axios.get(`${API_BASE}/profile`, { headers: getHeaders() });
    return data;
  }

  static async updateProfile(payload) {
    const { data } = await axios.put(`${API_BASE}/profile`, payload, { headers: getHeaders() });
    return data;
  }

  static async changePassword({ currentPassword, newPassword }) {
    const { data } = await axios.put(`${API_BASE}/change-password`, { currentPassword, newPassword }, { headers: getHeaders() });
    return data;
  }

  // SII
  static async getCompras({ month, year }) {
    const { data } = await axios.get(`${API_BASE}/sii/compras?month=${month}&year=${year}`, { headers: getHeaders() });
    return data;
  }

  static async getVentas({ month, year }) {
    const { data } = await axios.get(`${API_BASE}/sii/ventas?month=${month}&year=${year}`, { headers: getHeaders() });
    return data;
  }

  static async getResumen({ year }) {
    const { data } = await axios.get(`${API_BASE}/sii/resumen?year=${year}`, { headers: getHeaders() });
    return data;
  }

  // SII Credentials
  static async getSiiCredentials() {
    const { data } = await axios.get(`${API_BASE}/sii/credentials`, { headers: getHeaders() });
    return data;
  }

  static async saveSiiCredentials({ rut, clave, companyRut }) {
    const { data } = await axios.post(`${API_BASE}/sii/credentials`, { rut, clave, companyRut }, { headers: getHeaders() });
    return data;
  }

  static async deleteSiiCredentials() {
    const { data } = await axios.delete(`${API_BASE}/sii/credentials`, { headers: getHeaders() });
    return data;
  }

  static async testSiiCredentials({ rut, clave }) {
    const { data } = await axios.post(`${API_BASE}/sii/test-credentials`, { rut, clave }, { headers: getHeaders() });
    return data;
  }

  // ─── DTE PDFs ────────────────────────────────────────────────
  static async triggerDteSync({ month, year, direction = "BOTH", maxDocuments = 50 }) {
    const { data } = await axios.post(
      `${API_BASE}/sii/dtes/sync`,
      { month, year, direction, maxDocuments },
      { headers: getHeaders() }
    );
    return data;
  }

  static async getDtes({ month, year, direction } = {}) {
    const params = {};
    if (month) params.month = month;
    if (year) params.year = year;
    if (direction) params.direction = direction;
    const { data } = await axios.get(`${API_BASE}/sii/dtes`, { params, headers: getHeaders() });
    return data;
  }

  static async getDteViewUrl(dteId) {
    const { data } = await axios.get(`${API_BASE}/sii/dtes/${dteId}/view-url`, { headers: getHeaders() });
    return data;
  }

  static async getDteDownloadUrl(dteId) {
    const { data } = await axios.get(`${API_BASE}/sii/dtes/${dteId}/download-url`, { headers: getHeaders() });
    return data;
  }

  static async getHistorico({ fromYear, toYear } = {}) {
    const params = {};
    if (fromYear) params.fromYear = fromYear;
    if (toYear) params.toYear = toYear;
    const { data } = await axios.get(`${API_BASE}/sii/historico`, { params, headers: getHeaders() });
    return data;
  }

  // ─── Sprint Portal Dashboard rediseño (3 métodos) ─────────────
  static async getVentasHoy() {
    const { data } = await axios.get(`${API_BASE}/sii/ventas/hoy`, { headers: getHeaders() });
    return data;
  }

  static async getComprasHoy() {
    const { data } = await axios.get(`${API_BASE}/sii/compras/hoy`, { headers: getHeaders() });
    return data;
  }

  static async getIvaAPagar({ year, month } = {}) {
    const params = new URLSearchParams();
    if (year) params.set("year", year);
    if (month) params.set("month", month);
    const qs = params.toString() ? `?${params}` : "";
    const { data } = await axios.get(`${API_BASE}/iva-a-pagar${qs}`, { headers: getHeaders() });
    return data;
  }

  static async getPortalIndicators({ year, month } = {}) {
    const params = new URLSearchParams();
    if (year) params.set("year", year);
    if (month) params.set("month", month);
    const qs = params.toString() ? `?${params}` : "";
    const { data } = await axios.get(`${API_BASE}/indicators${qs}`, { headers: getHeaders() });
    return data;
  }

  // SII Sync
  static async triggerSync({ month, year, direction = "BOTH" }) {
    const { data } = await axios.post(`${API_BASE}/sii/sync`, { month, year, direction }, { headers: getHeaders() });
    return data;
  }

  static async getSyncHistory() {
    const { data } = await axios.get(`${API_BASE}/sii/sync/history`, { headers: getHeaders() });
    return data;
  }

  static async getSyncStatus(syncId) {
    const { data } = await axios.get(`${API_BASE}/sii/sync/${syncId}`, { headers: getHeaders() });
    return data;
  }

  static async cancelSync(syncId) {
    const { data } = await axios.post(`${API_BASE}/sii/sync/${syncId}/cancel`, {}, { headers: getHeaders() });
    return data;
  }

  // Notifications
  static async getNotifications({ page = 1 } = {}) {
    const { data } = await axios.get(`${API_BASE}/notifications?page=${page}`, { headers: getHeaders() });
    return data;
  }

  static async markNotificationRead(id) {
    const { data } = await axios.put(`${API_BASE}/notifications/${id}/read`, {}, { headers: getHeaders() });
    return data;
  }

  static async getUnreadCount() {
    const { data } = await axios.get(`${API_BASE}/notifications/unread-count`, { headers: getHeaders() });
    return data;
  }

  // Documents
  static async getDocuments() {
    const { data } = await axios.get(`${API_BASE}/documents`, { headers: getHeaders() });
    return data;
  }

  static async getDocumentFolderFiles(folderId) {
    const { data } = await axios.get(`${API_BASE}/documents/folder/${folderId}/files`, { headers: getHeaders() });
    return data;
  }

  // Admin: portal management (uses admin Basic Auth)
  static getAdminHeaders() {
    return { Authorization: `Basic ${process.env.REACT_APP_BASIC_AUTH}` };
  }

  static async inviteMember(memberId) {
    const { data } = await axios.post(`${API_BASE}/admin/invite`, { memberId }, { headers: this.getAdminHeaders() });
    return data;
  }

  static async togglePortalAccess(memberId) {
    const { data } = await axios.post(`${API_BASE}/admin/toggle-access`, { memberId }, { headers: this.getAdminHeaders() });
    return data;
  }

  static async getPortalStatus(memberId) {
    const { data } = await axios.get(`${API_BASE}/admin/portal-status/${memberId}`, { headers: this.getAdminHeaders() });
    return data;
  }

  static async listPortalMembers({ page = 1, limit = 25, search = "" } = {}) {
    const { data } = await axios.get(`${API_BASE}/admin/list`, {
      params: { page, limit, search },
      headers: this.getAdminHeaders(),
    });
    return data;
  }

  static async impersonateMember(memberId) {
    const { data } = await axios.post(
      `${API_BASE}/admin/impersonate/${memberId}`,
      {},
      { headers: this.getAdminHeaders() }
    );
    return data;
  }

  static async createPortalUser(memberId, { email, password } = {}) {
    const body = { email };
    if (password) body.password = password;
    const { data } = await axios.post(
      `${API_BASE}/admin/create-user/${memberId}`,
      body,
      { headers: this.getAdminHeaders() }
    );
    return data;
  }

  // ─── Expenses / Gastos ─────────────────────────────────────────
  static async uploadExpense(file, onProgress) {
    // Usamos XHR para tener progress events. Axios v1 también soporta pero
    // mantenemos consistencia con services/gdrive.js.
    return new Promise((resolve, reject) => {
      const form = new FormData();
      form.append("file", file);
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API_BASE}/expenses`);
      const headers = getHeaders();
      Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        try {
          const body = JSON.parse(xhr.responseText || "{}");
          if (xhr.status >= 200 && xhr.status < 300) resolve(body);
          else reject(new Error(body.error || `HTTP ${xhr.status}`));
        } catch (e) {
          reject(new Error(`Respuesta inválida: ${e.message}`));
        }
      };
      xhr.onerror = () => reject(new Error("Error de red"));
      xhr.send(form);
    });
  }

  static async listExpenses({ status, month, year, limit } = {}) {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (month) params.set("month", month);
    if (year) params.set("year", year);
    if (limit) params.set("limit", limit);
    const { data } = await axios.get(`${API_BASE}/expenses?${params.toString()}`, { headers: getHeaders() });
    return data;
  }

  static async getExpenseViewUrl(id) {
    const { data } = await axios.get(`${API_BASE}/expenses/${id}/view-url`, { headers: getHeaders() });
    return data;
  }

  static async deleteExpense(id) {
    const { data } = await axios.delete(`${API_BASE}/expenses/${id}`, { headers: getHeaders() });
    return data;
  }
}
